/**
 * 🧱 Refactor BT Phase 3 — Étape 2 : section « SectionEquipeHeures » d'EditerBonTravailDialog.jsx.
 * JSX déplacé TEL QUEL (ex-L1089-1550, dédenté de 10) ; variables lues via `useBTForm()`
 * (BTFormProvider enrichi par `extra` dans le composant parent). DOM, data-testid, classes, textes inchangés.
 */
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Calendar, Lock, Plus, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CoutEmployeurBadge } from '../../CoutEmployeurBadge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { calculerCoutEquipe, calculerHeuresBudgetees, getCoutEmployeur } from '../../utils';
import { useBTForm } from '../../BTFormContext';

export function SectionEquipeHeures() {
  const {
    bon,
    employees,
    employesAyantPunche,
    estVisibleDansListeEquipe,
    fermerDialogSuppression,
    form,
    getEmpDates,
    getMontantContrat,
    handleChefChange,
    isProprietaire,
    lockedInfo,
    membresExclus,
    membresExtras,
    membresMatrice,
    ouvrirDialogSuppression,
    setChefDuJour,
    setExtrasRetiresManuellement,
    setForm,
    setMembresExclus,
    setMembresExtras,
    setShowAddExtraMembre,
    showAddExtraMembre,
    toggleEmployeJour,
    type,
    user,
  } = useBTForm();
  return (
    <>
      {/* Chef d'équipe + Heures */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Chef d'équipe / Responsable</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-2 items-center">
              <Select value={form.chef_equipe_id || undefined} onValueChange={handleChefChange}>
                <SelectTrigger className="flex-1" data-testid="select-chef-equipe">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {/* 🐛 [Phase v13.46 — 2026-07-14] Option "Sans chef" en tête + filtre
                      strict des chefs uniquement (type_employe OU role_terrain). Post-Phase 107,
                      la sélection chef ne doit PLUS proposer les non-chefs. */}
                  <SelectItem value="__none__" data-testid="chef-option-sans-chef">— Sans chef —</SelectItem>
                  {(() => {
                    const isProprio = (e) => e.est_proprietaire === true || e.role === 'proprietaire';  // E3b-whitelist : drapeau est_proprietaire de la fiche (donnée)
                    const isChef = (e) => e.type_employe === 'chef_equipe' || e.role_terrain === 'chef_equipe' || isProprio(e);
                    const isVisible = (e) => e.est_actif !== false || isProprio(e);
                    const chefs = employees.filter(e => isVisible(e) && isChef(e));
                    return (
                      <>
                        {chefs.length > 0 && (
                          <div className="px-2 py-1 text-[10px] font-semibold text-slate-400 uppercase border-t mt-1 pt-1">Chefs d'équipe</div>
                        )}
                        {chefs.map(emp => (
                          <SelectItem key={emp.id} value={emp.id} data-testid={`chef-option-${emp.id}`}>
                            {emp.prenom} {emp.nom}{isProprio(emp) ? ' 👑' : ''}
                          </SelectItem>
                        ))}
                        {chefs.length === 0 && (
                          <div className="px-2 py-2 text-xs text-slate-500 italic">Aucun chef d'équipe disponible</div>
                        )}
                      </>
                    );
                  })()}
                </SelectContent>
              </Select>
              {form.chef_equipe_id && (
                <Button type="button" variant="ghost" size="sm" className="text-red-400 hover:text-red-600 px-2" onClick={() => setForm(f => ({ ...f, chef_equipe_id: '', chef_equipe_nom: '' }))} title="Retirer le chef d'équipe">
                  <X className="w-4 h-4" />
                </Button>
              )}
              {/* 🐛 [Phase v13.46 — 2026-07-14] Badge visuel FR-CA quand aucun chef n'est assigné */}
              {!form.chef_equipe_id && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-300 whitespace-nowrap"
                  data-testid="badge-chef-a-assigner"
                  title="Un chef d'équipe est fortement recommandé pour ce bon de travail"
                >
                  ⚠️ Chef à assigner
                </span>
              )}
            </div>
            {/* 🗑️ [Phase 51 cleanup 2026-06-08] CollisionChecker retiré du formulaire BT.
                L'utilisatrice a explicitement spécifié que les conflits d'horaire
                doivent être gérés UNIQUEMENT depuis le calendrier de production
                (badges Position Phase 51 BIS A). Le BT garde uniquement l'alerte
                "Conflit de congé" (en dessous, bloc séparé indépendant). */}
            {/* Membres de l'équipe — toujours visible pour permettre l'ajout d'extras
                même quand "Aucun" chef est sélectionné (cas d'un BT sans équipe principale). */}
            {(() => {
              // 🚀 [Phase 107.B — 2026-07-14] Fin des équipes prédéterminées.
              //   Avant : membres = employees.filter(e.chef_equipe_id === form.chef_equipe_id).
              //   Maintenant : "matrice" propose TOUS les employés terrain actifs
              //   (sauf chef principal, spécialistes lavage, sous-traitants, installateurs).
              //   L'utilisatrice sélectionne manuellement qui travaille chaque jour.
              const ROLES_EXCLUS_MO = ['sous_traitant', 'installateur'];
              const isTerrainMO = (e) => {
                const rt = String(e.role_terrain || '').toLowerCase();
                const te = String(e.type_employe || '').toLowerCase();
                if (ROLES_EXCLUS_MO.includes(rt) || ROLES_EXCLUS_MO.includes(te)) return false;
                return rt === 'peintre' || rt === 'chef_equipe' || te === 'peintre' || te === 'chef_equipe';
              };
              const membres = employees.filter(e =>
                isTerrainMO(e)
                && e.est_actif !== false
         
                && e.id !== form.chef_equipe_id
              );
              // 🛡️ [Phase 61 — 2026-06-17] Source unique de vérité « matrice » :
              // un membre officiel est affiché dans la liste à gauche UNIQUEMENT s'il
              // n'est pas exclu ET s'il a au moins 1 jour coché dans la matrice
              // (ou s'il n'a jamais été touché → implicite). Dès que la dernière
              // case d'un membre est décochée, il disparaît visuellement de la liste.
              //
              // 🚀 [Phase v13.46.4 — 2026-07-17] REFONTE — Retrait des vestiges
              // Un membre officiel n'est LÉGITIME que s'il :
              //   - a réellement punché sur ce BT (employesAyantPunche), OU
              //   - a été ajouté manuellement (membresExtras)
              // Un vestige `bon.employes_assignes` sans punch ni ajout manuel n'apparaît PAS.
              const idsLegitimes = new Set([
                ...(employesAyantPunche || []).map(p => p.employee_id).filter(Boolean),
                ...(membresExtras || []).map(e => e.id).filter(Boolean),
              ]);
              const membresActifs = membres.filter(m =>
                !membresExclus.has(m.id)
                && estVisibleDansListeEquipe(m.id)
                && idsLegitimes.has(m.id)
              );
              // 🚀 [Phase v13.46.5 — 2026-07-18] Option C — Dédup au rendu.
              // Bug prod : un employé ajouté via dropdown apparaissait DEUX FOIS
              //   (1 chip grise via `membresActifs` + 1 chip mauve via `membresExtras`).
              // Fix : si l'employé est DÉJÀ rendu dans `membresActifs` (chip grise),
              //   on le retire de `membresExtras` au moment du rendu (chip mauve).
              // On ne touche PAS aux tableaux de state ; c'est un filtre visuel pur.
              const membresActifsIds = new Set(membresActifs.map(m => m.id));
              const membresExtrasActifs = membresExtras.filter(me =>
                estVisibleDansListeEquipe(me.id)
                && !membresActifsIds.has(me.id)
              );
              // Handler unifié — retire un membre officiel après confirmation
              const supprimerMembreOfficiel = (m) => {
                ouvrirDialogSuppression('officiel', m.id, `${m.prenom} ${m.nom}`.trim(), () => {
                  // Confirmation : exclure du BT + vider toutes ses cases dans presence
                  setMembresExclus(prev => { const next = new Set(prev); next.add(m.id); return next; });
                  setForm(f => {
                    const newAssignes = (f.employes_assignes || []).map(e =>
                      e.employee_id === m.id ? { ...e, dates_assignees: [] } : e
                    );
                    // Retirer les chef_du_jour pointant vers cet employé (au cas où)
                    const newChefs = { ...(f.chefs_par_jour || {}) };
                    Object.keys(newChefs).forEach(d => { if (newChefs[d] === m.id) delete newChefs[d]; });
                    return { ...f, employes_assignes: newAssignes, chefs_par_jour: newChefs };
                  });
                  fermerDialogSuppression();
                });
              };
              // Handler unifié — retire un membre extra après confirmation
              const supprimerMembreExtra = (me) => {
                ouvrirDialogSuppression('extra', me.id, `${me.prenom} ${me.nom}`.trim(), () => {
                  setMembresExtras(prev => prev.filter(x => x.id !== me.id));
                  setExtrasRetiresManuellement(prev => { const next = new Set(prev); next.add(me.id); return next; });
                  setForm(f => {
                    const newAssignes = (f.employes_assignes || []).filter(e => e.employee_id !== me.id);
                    const newChefs = { ...(f.chefs_par_jour || {}) };
                    Object.keys(newChefs).forEach(d => { if (newChefs[d] === me.id) delete newChefs[d]; });
                    return { ...f, employes_assignes: newAssignes, chefs_par_jour: newChefs };
                  });
                  fermerDialogSuppression();
                });
              };
              return (
                <div className="mt-2">
                  {form.chef_equipe_id ? (
                    <p className="text-xs text-slate-500 mb-1">Membres de l'équipe ({membresActifs.length + 1} pers.) :</p>
                  ) : (
                    <p className="text-xs text-slate-500 mb-1">Employés assignés ({membresExtrasActifs.length} pers.) :</p>
                  )}
                  {form.chef_equipe_id && membresActifs.length === 0 && (
                    <p className="text-xs text-slate-400 italic">Aucun peintre cédulé sur ce BT pour l'instant.</p>
                  )}
                  <div className="flex flex-wrap gap-1">
                    {membresActifs.map(m => (
                      <span key={m.id} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700" data-testid={`chip-membre-${m.id}`}>
                        {/* 🚀 [Phase v13.46.4 — 2026-07-17] Retrait du point coloré `couleur_equipe`
                            (vestige Phase 107 — équipes abolies). Les chips restent neutres. */}
                        {m.prenom} {m.nom}
                        <button
                          type="button"
                          onClick={() => supprimerMembreOfficiel(m)}
                          className="ml-0.5 text-red-300 hover:text-red-500"
                          title="Retirer ce membre du BT"
                          data-testid={`btn-remove-membre-${m.id}`}
                        >×</button>
                      </span>
                    ))}
                  </div>
                  {/* Membres extra (d'autres équipes ou sans chef principal) */}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {membresExtrasActifs.map(me => (
                      <span key={me.id} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200" data-testid={`chip-extra-${me.id}`}>
                        {me.prenom} {me.nom}{me.est_proprietaire ? ' 👑' : ''}
                        <button
                          type="button"
                          onClick={() => supprimerMembreExtra(me)}
                          className="text-red-300 hover:text-red-500 ml-0.5"
                          data-testid={`btn-remove-extra-${me.id}`}
                        >×</button>
                      </span>
                    ))}
                  </div>
                  {/* Bouton ajouter un membre externe — disponible avec ou sans chef principal */}
                  {showAddExtraMembre ? (
                    <Select onValueChange={empId => {
                      const emp = employees.find(e => e.id === empId);
                      // 🧱 Phase 4 — Lot E (B-27) : garde-fou « déjà dans la liste » (matrice = chef + assignés/punchés + extras)
                      if (emp && (membresMatrice || []).some(m => m.id === empId)) {
                        toast.info(`${emp.prenom} ${emp.nom} est déjà dans la liste`, { id: 'msg-employe-deja-present' });
                        setShowAddExtraMembre(false);
                        return;
                      }
                      if (emp && !membresExtras.find(m => m.id === empId)) {
                        setMembresExtras(prev => [...prev, emp]);
                        // 🛡️ [Phase 57] Si l'employé était dans la liste des retraits
                        // explicites, on le retire (l'utilisatrice change d'avis).
                        setExtrasRetiresManuellement(prev => {
                          if (!prev.has(empId)) return prev;
                          const next = new Set(prev);
                          next.delete(empId);
                          return next;
                        });
                      }
                      setShowAddExtraMembre(false);
                    }}>
                      <SelectTrigger className="h-7 text-[10px] border-dashed mt-1"><SelectValue placeholder="Choisir un employe..." /></SelectTrigger>
                      <SelectContent>
                        {(() => {
                          const isProprio = (e) => e.est_proprietaire === true || e.role === 'proprietaire';  // E3b-whitelist : drapeau est_proprietaire de la fiche (donnée)
                          // 🚀 [Phase v13.46.4 — 2026-07-17] Le filtre exclut les employés
                          // DÉJÀ AFFICHÉS dans la matrice (membresActifs + extras + chef),
                          // pas TOUS les terrain (bug L1854 avant fix — filtrait trop large).
                          return employees.filter(e =>
                            (e.est_actif !== false || isProprio(e))
                            && e.id !== form.chef_equipe_id
                            && !membresActifs.find(m => m.id === e.id)
                            && !membresExtras.find(m => m.id === e.id)
                     
                          ).map(e => (
                            <SelectItem key={e.id} value={e.id} data-testid={`dropdown-emp-option-${e.id}`}>{e.prenom} {e.nom}{isProprio(e) ? ' 👑' : ''} {e.chef_equipe_id ? `(eq. ${employees.find(c => c.id === e.chef_equipe_id)?.prenom || ''})` : ''}</SelectItem>
                          ));
                        })()}
                      </SelectContent>
                    </Select>
                  ) : (
                    <button type="button" onClick={() => setShowAddExtraMembre(true)} className="flex items-center gap-1 text-[10px] text-purple-600 hover:text-purple-700 border border-dashed border-purple-300 rounded px-2 py-0.5 mt-1">
                      <Plus className="w-2.5 h-2.5" /> Ajouter d'une autre equipe
                    </button>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* 🐛 [Phase v13.46.2 — 2026-07-14] Matrice toujours visible : plus de gate
            `dates_chantier?.length > 1`. L'utilisatrice veut voir les employés
            apparaître dans la matrice DÈS qu'ils sont ajoutés (chef + extras +
            punchés), même sans dates saisies. */}
        <Card data-testid="matrice-presence-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              📅 Présence par jour
              <span className="text-xs font-normal text-slate-500">— qui travaille quel jour ?</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!form.dates_chantier || form.dates_chantier.length === 0) ? (
              <div
                className="text-sm text-slate-500 italic p-4 text-center border border-dashed border-slate-200 rounded"
                data-testid="matrice-empty-hint"
              >
                Ajoutez au moins une date de chantier ci-dessous pour marquer la présence de chaque employé.
              </div>
            ) : (
            <>
              <p className="text-xs text-slate-600 mb-3">
                Sélectionnez le chef d'équipe par jour (optionnel) et décochez les cases pour qu'un employé ne voit pas le BT certains jours.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left p-2 border-b border-slate-200 sticky left-0 bg-white z-10">Employé</th>
                      {form.dates_chantier.map(d => (
                        <th key={d} className="p-2 border-b border-slate-200 text-center min-w-[64px]">
                          {new Date(d + 'T12:00:00').toLocaleDateString('fr-CA', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* 🆕 Chef du jour — dropdown par jour */}
                    {(() => {
                      const allChefs = (employees || []).filter(e => (e.role_terrain === 'chef_equipe' || e.type_employe === 'chef_equipe') && e.est_actif !== false);
                      if (allChefs.length === 0) return null;
                      return (
                        <tr className="bg-amber-50">
                          <td className="p-2 border-b border-amber-100 sticky left-0 bg-amber-50 whitespace-nowrap z-10" data-testid="row-chef-du-jour">
                            <span className="text-amber-700 mr-1">⭐</span>
                            <span className="font-medium text-amber-900">Chef du jour</span>
                          </td>
                          {form.dates_chantier.map(d => {
                            // 🆕 2026-05-08 — "__none__" = Aucun explicite (pas de fallback)
                            const cpjVal = form.chefs_par_jour?.[d];
                            const chefId = cpjVal === '__none__'
                              ? '__none__'
                              : (cpjVal || form.chef_equipe_id || '');
                            return (
                              <td key={d} className="p-1 border-b border-amber-100 text-center">
                                <select
                                  value={chefId}
                                  onChange={(ev) => setChefDuJour(d, ev.target.value)}
                                  className="text-[10px] border border-amber-300 rounded px-1 py-0.5 bg-white max-w-[100px] focus:outline-none focus:ring-1 focus:ring-amber-500"
                                  data-testid={`chef-du-jour-${d}`}
                                >
                                  <option value="__none__">— Aucun —</option>
                                  {allChefs.map(c => (
                                    <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>
                                  ))}
                                </select>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })()}
                    {membresMatrice.map(m => {
                      const presents = getEmpDates(m.employee_id, m.est_chef);
                      const empLocked = (lockedInfo?.locked_per_employee || {})[m.employee_id] || [];
                      const empAbsent = (lockedInfo?.absent_per_employee || {})[m.employee_id] || [];
                      return (
                        <tr key={m.employee_id} className="hover:bg-slate-50">
                          <td className="p-2 border-b border-slate-100 sticky left-0 bg-white whitespace-nowrap z-10">
                            {m.est_chef && <span className="text-purple-600 mr-1">👑</span>}
                            {m.employee_nom}
                          </td>
                          {form.dates_chantier.map(d => {
                            const isLocked = empLocked.includes(d);   // a punché
                            const isAbsent = empAbsent.includes(d);   // pas punché, jour gravé
                            // Présent affichage : verrouillé en présent OU coché libre (et pas verrouillé en absent)
                            const present = isLocked || (!isAbsent && presents.includes(d));
                            let btnClass, btnContent, btnTitle;
                            if (isLocked) {
                              btnClass = 'w-7 h-7 rounded bg-emerald-100 text-emerald-700 border border-emerald-300 cursor-not-allowed';
                              btnContent = '🔒';
                              btnTitle = "🔒 Présent (heures enregistrées — verrouillé)";
                            } else if (isAbsent) {
                              btnClass = 'w-7 h-7 rounded bg-slate-100 text-slate-400 border border-slate-300 cursor-not-allowed';
                              btnContent = '🚫';
                              btnTitle = "🚫 Absent gravé : pas de punch ce jour, journée déjà validée";
                            } else if (present) {
                              btnClass = 'w-7 h-7 rounded bg-emerald-500 text-white hover:bg-emerald-600 transition';
                              btnContent = '✓';
                              btnTitle = 'Cliquer pour retirer ce jour';
                            } else {
                              btnClass = 'w-7 h-7 rounded bg-slate-200 text-slate-400 hover:bg-slate-300 transition';
                              btnContent = '—';
                              btnTitle = 'Cliquer pour assigner ce jour';
                            }
                            return (
                              <td key={d} className="p-1 border-b border-slate-100 text-center">
                                <button
                                  type="button"
                                  onClick={() => toggleEmployeJour(m.employee_id, d, m.est_chef)}
                                  disabled={isLocked || isAbsent}
                                  className={btnClass}
                                  data-testid={`presence-${m.employee_id}-${d}`}
                                  title={btnTitle}
                                >
                                  {btnContent}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                💡 Sélectionner un chef pour un jour ajoute automatiquement le chef et ses peintres à l'équipe ce jour-là. Le chef principal du BT est le chef avec le plus de jours assignés.
              </p>
            </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Heures budgétées</CardTitle></CardHeader>
          <CardContent>
            {/* 🚀 [Phase 107.B — 2026-07-14] Coût employeur dynamique */}
            {bon?.id && (
              <div className="mb-3">
                <CoutEmployeurBadge
                  btId={bon.id}
                  currentUser={{ role: isProprietaire ? 'proprietaire' : (user?.role || '') }}
                  dataTestId="editer-bt-cout-employeur"
                />
              </div>
            )}
            <Input 
              type="number" 
              value={form.heures_budgetees} 
              onChange={(e) => isProprietaire && setForm(f => ({ ...f, heures_budgetees: e.target.value, heures_budgetees_ajustees_manuellement: true }))} 
              placeholder="Ex: 40"
              disabled={!isProprietaire}
              className={!isProprietaire ? 'bg-gray-100' : ''}
            />
            {!isProprietaire && <p className="text-xs text-gray-500 mt-1">Calcul automatique basé sur l'équipe assignée</p>}
            {isProprietaire && form.heures_budgetees_ajustees_manuellement && (
              <div className="flex items-center justify-between gap-2 mt-1">
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Valeur ajustée manuellement — protégée des recalculs automatiques
                </p>
                <button
                  type="button"
                  data-testid="reset-heures-auto-btn"
                  onClick={() => setForm(f => ({ ...f, heures_budgetees_ajustees_manuellement: false }))}
                  className="text-[10px] text-blue-600 hover:text-blue-800 underline shrink-0"
                  title="Réactiver le calcul automatique selon l'équipe"
                >
                  Recalculer auto
                </button>
              </div>
            )}
            {/* Calcul auto + estimation jours */}
            {(() => {
              const montant = getMontantContrat();
              const chefId = form.chef_equipe_id;
              if (!chefId) return <p className="text-xs text-amber-600 mt-1">Sélectionnez un chef d'équipe</p>;
        
              const equipe = calculerCoutEquipe(chefId, employees, form.dates_chantier?.[0]);
              if (montant <= 0) return <p className="text-xs text-amber-600 mt-1">Montant du contrat manquant</p>;
              if (equipe.coutMoyen <= 0) return <p className="text-xs text-amber-600 mt-1">Coût employeur non configuré dans les fiches</p>;
        
              // ✅ FIX (2026-04-20): Calcul du coût moyen avec la date du chantier (historique salaires)
              //   + inclusion correcte des membres extras (bug : ils étaient dans nbMembres mais pas dans la somme)
              const dateRef = form.dates_chantier?.[0];
              const membres = employees.filter(e => e.chef_equipe_id === chefId && e.est_actif !== false && !membresExclus.has(e.id));
              const chef = employees.find(e => e.id === chefId);
        
              // Liste complète des employés actifs sur la job : chef + membres équipe (non exclus) + extras
              const tousLesEmployes = [];
              if (chef) tousLesEmployes.push(chef);
              tousLesEmployes.push(...membres);
              tousLesEmployes.push(...membresExtras);
        
              const nbMembres = tousLesEmployes.length;
              let coutTotal = 0;
              for (const m of tousLesEmployes) {
                coutTotal += getCoutEmployeur(m, dateRef);
              }
              const coutMoyen = nbMembres > 0 && coutTotal > 0 ? coutTotal / nbMembres : equipe.coutMoyen;
        
              const calc = calculerHeuresBudgetees(montant, coutMoyen, nbMembres, form.dates_chantier);
              if (calc.heures <= 0) return null;

              // Détail pour tooltip (transparence sur le coût moyen)
              const detailCoutTooltip = tousLesEmployes
                .map(m => `${m.prenom || ''} ${m.nom || ''}: ${getCoutEmployeur(m, dateRef).toFixed(2)} $/h`)
                .join('\n') + `\n\nMoyenne : ${(coutTotal / nbMembres).toFixed(2)} $/h (${nbMembres} employés)`;
        
              return (
                <div className="mt-1.5 space-y-1">
                  {isProprietaire ? (
                    <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1" data-testid="heures-proposees-edit" title={detailCoutTooltip}>
                      {calc.montantHT?.toLocaleString('fr-CA',{style:'currency',currency:'CAD'})} (sans taxes) x 45% / <span className="underline decoration-dotted cursor-help">{calc.coutMoyen?.toFixed(2)}$/h</span> = <strong>{calc.heures}h totales</strong>
                    </p>
                  ) : (
                    <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1" data-testid="heures-proposees-edit">
                      Heures budgétées : <strong>{calc.heures}h totales</strong>
                    </p>
                  )}
                  {calc.heures > 0 && (
                    <p className="text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1.5 flex items-center gap-1" data-testid="estimation-jours-edit">
                      <Calendar className="w-3.5 h-3.5 shrink-0" />
                      Job à faire en ~{calc.jours} jour(s) ({nbMembres} employé{nbMembres > 1 ? 's' : ''})
                      <span className="text-blue-500 font-normal ml-1">({calc.heures}h totales / {calc.heuresParJour}h×{nbMembres} — saison {calc.saison})</span>
                    </p>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>

    </>
  );
}
